<script setup lang="ts">
import ProjectList from '@/pages/banks/ProjectList.vue'
import { useFeatureFlagStore } from '@/stores/features'
import ProjectsController from '@controllers/Banks/ProjectsController'
import { ChartData } from 'chart.js'
import Button from 'primevue/button'
import Chart from 'primevue/chart'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProjectSuggestionData = App.Data.Bank.ProjectSuggestionData
import FinestCard from '@/components/FinestCard.vue'

const { t } = useI18n()
const { enabled } = useFeatureFlagStore()

const props = defineProps<{
  project_suggestions: Array<ProjectSuggestionData>
  number_of_project_suggestions: number
  loan_negotiation_projects: Array<ProjectSuggestionData>
  number_of_loan_negotiation_projects: number
  investVolumeDistribution: ChartData<'pie'>
}>()

const translatedNewProjectsPreview = computed(() => {
  return props.project_suggestions.map(
    (projectSuggestion) =>
      ({
        ...projectSuggestion,
        estate_objects: projectSuggestion.estate_objects.map((eo) => ({
          ...eo,
          estate_object_type: t(`enums.estateObjectType.${eo.estate_object_type}`),
          owner_type: t(`enums.personType.${eo.owner_type}`),
        })),
      }) as ProjectSuggestionData,
  )
})

const translatedActiveProjectsPreview = computed(() => {
  return props.loan_negotiation_projects.map(
    (projectSuggestion) =>
      ({
        ...projectSuggestion,
        estate_objects: projectSuggestion.estate_objects.map((eo) => ({
          ...eo,
          estate_object_type: t(`enums.estateObjectType.${eo.estate_object_type}`),
          owner_type: t(`enums.personType.${eo.owner_type}`),
        })),
      }) as ProjectSuggestionData,
  )
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
}
</script>

<template>
  <div>
    <FinestCard :title="t('welcome_to_finesthub')" icon="pi pi-info-circle">
      <p>
        {{ t('welcome_message') }}
      </p>
    </FinestCard>
    <div class="mt-6 grid grid-cols-12 gap-6">
      <Card v-if="enabled('financing_requests')" class="col-span-9">
        <template #title>
          {{ t('project_suggestions') }}
        </template>
        <template #content>
          <ProjectList :projects="translatedNewProjectsPreview" />
        </template>
        <template #footer>
          <div v-if="number_of_project_suggestions > 0" class="flex justify-end">
            <Link :href="ProjectsController.suggested()">
              <Button
                icon="pi pi-arrow-right"
                icon-pos="right"
                severity="secondary"
                :label="t('show_all_projects', { count: number_of_project_suggestions })" />
            </Link>
          </div>
        </template>
      </Card>

      <Card v-if="enabled('dashboard_investment_volume_chart')" class="col-span-3 row-span-2">
        <template #title>
          {{ t('investment_volume_distribution') }}
        </template>
        <template #content>
          <Chart type="pie" :data="investVolumeDistribution" :options="chartOptions" class="h-100 w-full" />
        </template>
      </Card>

      <Card v-if="enabled('loan_negotiations')" class="col-span-9">
        <template #title>
          {{ t('in_loan_negotiations') }}
        </template>
        <template #content>
          <ProjectList :projects="translatedActiveProjectsPreview" in-negotiations />
        </template>
        <template #footer>
          <div v-if="number_of_loan_negotiation_projects > 0" class="flex justify-end">
            <Link :href="ProjectsController.index()">
              <Button
                icon="pi pi-arrow-right"
                icon-pos="right"
                severity="secondary"
                :label="t('show_all_projects', { count: number_of_loan_negotiation_projects })" />
            </Link>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "welcome_to_finesthub": "Welcome to FinEstHub!",
    "welcome_message": "Welcome to FinEstHub!",
    "project_suggestions": "Project suggestions",
    "in_loan_negotiations": "In loan negotiations",
    "investment_volume_distribution": "Investment Volume Distribution",
    "show_all_projects": "Show all {count} projects"
  },
  "de": {
    "welcome_to_finesthub": "Herzlich willkommen bei FinEstHub!",
    "welcome_message": "Herzlich willkommen bei FinEstHub!",
    "project_suggestions": "Finanzierungsanfragen",
    "in_loan_negotiations": "In Finanzierungsverhandlung",
    "investment_volume_distribution": "Verteilung des Investmentvolumens",
    "show_all_projects": "Alle {count} Projekte anzeigen"
  }
}
</i18n>
